import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom"; // Import useNavigate
import { handleError, handleSuccess } from "../../../Utils";
import { api, updateSlotCount } from "../../../ApiServices/ApiServices";

function PreviewBookingDetails() {
  const navigate = useNavigate(); // Initialize navigate
  const [selectedAmount, setSelectedAmount] = useState(null); // Default amount
  const [customAmount, setCustomAmount] = useState("");
  const [platformFee, setPlatformFee] = useState(10);
  const [receiveDetails, setReceiveDetails] = useState(false);

  useEffect(()=>{
    document.title = "CodeQueue - Booking preview";
  })

  const location = useLocation();
  const state = location.state;
  // If state is missing, redirect to the booking page or show an error
  if (!state) {
    return <Navigate to="/mentors-list" />;
  }
  const {
    mentorId,
    selectedPriceIndex,
    slotDate,
    selectedDateIndex,
    selectedTimeIndex,
    userEmail,
    userName,
    mentorName,
    sessionType,
    profileImage,
  } = location.state || {};

  const totalAmount =
    (parseInt(selectedAmount) || parseInt(customAmount) || 0) +
    platformFee +
    sessionType[selectedPriceIndex].amount;

  // Handle amount selection
  const handleAmountClick = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount(""); // Clear custom amount when selecting a predefined amount
  };

  // Handle custom amount input
  const handleCustomAmountChange = (e) => {
    setCustomAmount(e.target.value);
    setSelectedAmount(null); // Clear predefined amount when entering a custom amount
  };

  // Confirm payment
  const handleCreateOrder = async () => {
    const response = await api.post("/create-order", { amount: totalAmount });
    // verify order
    handleVerifyOrder(response.data.data);
  };

  const handleVerifyOrder = async (data) => {
    // const razorpayKeyId = process.env.REACT_APP_RAZORPAY_KEY_ID;
    const razorpayKeyId = 'rzp_live_aiGKQHLKnNOrz8';
    const options = {
      key: razorpayKeyId,
      amount: data.amount,
      currency: data.currency,
      name: "CodeQueue",
      description: "CodeQueue",
      order_id: data.id,
      handler: async (response) => {
        try {
          const res = await api.post("/verify-order", {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            session_title: sessionType[selectedPriceIndex].title, // Example session details
            amount: {
              service_amount: parseInt(sessionType[selectedPriceIndex].amount),
              platform_fee: parseInt(platformFee),
              additional_amount:
                parseInt(selectedAmount) || parseInt(customAmount) || 0,
            },
            scheduledDate: slotDate[selectedDateIndex].date,
            time: slotDate[selectedDateIndex].time[selectedTimeIndex].value,
            day: slotDate[selectedDateIndex].day,
            duration: sessionType[selectedPriceIndex].duration,
            user_email: userEmail,
            user_name: userName,
            mentorId,
          });

          // call the api to update the available slots 
          const formData = {};
          formData.id = mentorId;
          formData.day = slotDate[selectedDateIndex].day;
          formData.time = slotDate[selectedDateIndex].time[selectedTimeIndex].value;
          formData.is_booked = true;
          await updateSlotCount(formData);

          const verifyData = res.data;
          if (verifyData.message) {
            handleSuccess(verifyData.message);
          }
          navigate("/dashboard/bookings");
        } catch (error) {
          return handleError(error.response.data.message);
        }
      },
      theme: {
        color: "#5f63b8",
      },
    };
    const rzp1 = new window.Razorpay(options);
    rzp1.open();
  };

  return (
    <div className="min-h-screen flex flex-col bg-blue-900  py-5">
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto pb-20">
        {" "}
        {/* Added bottom padding for fixed footer */}
        <div className="bg-white rounded-lg shadow-lg w-full max-w-lg mx-auto p-6">
          {/* Header */}
          <div className="flex items-center space-x-4">
            {/* ArrowBackIcon */}
            <ArrowBackIcon
              className="text-black cursor-pointer"
              onClick={() => navigate(-1)} // Navigate back to the previous page
            />
            {/* Image */}
            <img
              src={profileImage}
              alt="User Profile"
              className="rounded-full w-10 h-10"
            />
            {/* Title */}
            <h1 className="font-medium text-lg">{mentorName}</h1>
          </div>

          {/* Webinar Info */}
          <div className="mt-6">
            <h2 className="text-xl font-bold">
              {sessionType[selectedPriceIndex].title}
            </h2>
            <p className="text-gray-600">
              Video Call | {sessionType[selectedPriceIndex].duration} Min
            </p>
            <div className="flex items-center justify-between mt-4 bg-gray-200 rounded-lg px-4 py-3">
              <div className="flex items-center">
                <div className="text-center">
                  <p className="text-sm font-bold">
                    {slotDate[selectedDateIndex].date}
                  </p>
                  {/* <p className="text-lg font-bold">{dateStr}</p> */}
                </div>
                <div className="ml-4">
                  <p className="font-medium">
                    {slotDate[selectedDateIndex].day}
                  </p>
                  <p className="text-sm text-gray-500">
                    {slotDate[selectedDateIndex].time[selectedTimeIndex].value}
                  </p>
                </div>
              </div>
              <button onClick={() => navigate(-1)} className="text-blue-600 font-medium hover:underline">
                Change
              </button>
            </div>
          </div>

          {/* User Details */}
          <div className="mt-6">
            <h3 className="text-[18px] ">{userName}</h3>
            <p className="text-[18px]">{userEmail}</p>
            {/* <div className="mt-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={receiveDetails}
                  onChange={() => setReceiveDetails(!receiveDetails)}
                  className="mr-2"
                />
                <span>Receive booking details on phone</span>
              </label>
            </div> */}
          </div>

          {/* Payment Options */}
          <div className="mt-6">
            <h3 className="text-lg font-bold mb-4">Pay what you like</h3>
            <div className="grid grid-cols-5 gap-2">
              {[100, 200, 300, 400, 500].map((amount) => (
                <button
                  key={amount}
                  className={`px-4 py-2 rounded-lg text-center ${selectedAmount === amount
                      ? "bg-green-600 text-white"
                      : "bg-gray-200"
                    }`}
                  onClick={() => handleAmountClick(amount)}
                >
                  ₹{amount}
                </button>
              ))}
              <input
                type="number"
                placeholder="Other"
                value={customAmount}
                onChange={handleCustomAmountChange}
                className="border rounded-lg px-4 py-2 col-span-5"
              />
            </div>
          </div>

          {/* Order Summary */}
          <div className="mt-6">
            <h3 className="text-lg font-bold mb-4">Order Summary</h3>
            <div className="flex justify-between mb-2">
              <span>1 × {sessionType[selectedPriceIndex].title}</span>
              <span>₹{sessionType[selectedPriceIndex].amount}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span>Platform fee</span>
              <span>₹{platformFee}</span>
            </div>
            <div className="flex justify-between mb-4">
              <span>Additional amount</span>
              <span>₹{selectedAmount || customAmount || 0}</span>
            </div>
            <div className="flex justify-between font-bold">
              <span>Total</span>
              <span>₹{totalAmount}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="fixed bottom-0 left-0 w-full bg-white shadow-lg ">
        <div className="max-w-lg mx-auto p-4 flex justify-between">
          <button className="bg-gray-200 text-black px-4 py-3 rounded-lg flex-1 mr-2 text-center">
            ₹{totalAmount}
          </button>
          <button
            onClick={handleCreateOrder}
            className="bg-black text-white px-4 py-3 rounded-lg flex-1 ml-2 text-center"
          >
            Confirm and Pay
          </button>
        </div>
      </div>
    </div>
  );
}

export default PreviewBookingDetails;
